import * as assets from '@assets';
import { capabilityImages, processmethodImages } from './Assets';
import { ProcessmethodCardProps } from '@routes/Home/Components/Cards/ProcessmethodCard';
import { CapabilityCardProps } from '@routes/Home/Components/Cards/CapabilityCard';
import {
  CapaPcbServiceType,
  CapaQuotationDetailedServiceType,
  CapaQuotationServiceType
} from '@generated/graphql';
import { Enum_Action_Button_Enum } from '@hooks/useUserActionLog';

interface Capabilities extends CapabilityCardProps {
  processmethodKey?: CapaQuotationServiceType;
}

export const capability: Capabilities[] = [
  {
    img: capabilityImages.CAPABILITY_CNC,
    label: 'CNC 가공',
    processmethodKey: CapaQuotationServiceType.Cnc,
    log: Enum_Action_Button_Enum.Landing_CNC
  },
  {
    img: capabilityImages.CAPABILITY_3DP,
    label: '3D 프린팅',
    processmethodKey: CapaQuotationServiceType.Printing,
    log: Enum_Action_Button_Enum.Landing_3DP
  },
  {
    img: capabilityImages.CAPABILITY_MOLDING,
    label: '금형사출',
    processmethodKey: CapaQuotationServiceType.Inj,
    log: Enum_Action_Button_Enum.Landing_INJ
  },
  {
    img: capabilityImages.CAPABILITY_SHEETMETAL,
    label: '판금가공',
    processmethodKey: CapaQuotationServiceType.SheetMetal,
    log: Enum_Action_Button_Enum.Landing_SM
  },
  {
    img: capabilityImages.CAPABILITY_CASTING,
    label: '주조',
    processmethodKey: CapaQuotationServiceType.Casting,
    log: Enum_Action_Button_Enum.Landing_CASTING
  },
  {
    img: capabilityImages.CAPABILITY_MD,
    label: '디자인/설계',
    processmethodKey: CapaQuotationServiceType.Design,
    log: Enum_Action_Button_Enum.Landing_DESIGN
  },
  {
    img: assets.ServiceType_Pcb_192,
    label: '전자회로(PCB)',
    processmethodKey: CapaQuotationServiceType.Pcb,
    log: Enum_Action_Button_Enum.Landing_PCB
  }
];

export const processmethodDetails: Record<
  CapaQuotationServiceType,
  ProcessmethodCardProps[]
> = {
  [CapaQuotationServiceType.Cnc]: [
    {
      img: processmethodImages.PROCESSMETHOD_CNC_MILLING,
      label: '밀링',
      english: 'Milling',
      serviceType: CapaQuotationServiceType.Cnc,
      detailedServiceType: CapaQuotationDetailedServiceType.Milling,
      log: Enum_Action_Button_Enum.Landing_CNC_MILLING
    },
    {
      img: processmethodImages.PROCESSMETHOD_CNC_TURNING,
      label: '선반',
      english: 'Turning',
      serviceType: CapaQuotationServiceType.Cnc,
      detailedServiceType: CapaQuotationDetailedServiceType.Turning,
      log: Enum_Action_Button_Enum.Landing_CNC_TURNING
    },
    {
      img: processmethodImages.PROCESSMETHOD_CNC_MACHINING_CENTER,
      label: '머시닝 센터',
      english: 'Machining Center',
      serviceType: CapaQuotationServiceType.Cnc,
      detailedServiceType: CapaQuotationDetailedServiceType.MachiningCenter,
      log: Enum_Action_Button_Enum.Landing_CNC_MC
    },
    {
      img: processmethodImages.PROCESSMETHOD_CNC_DRILLING,
      label: '드릴링',
      english: 'Drilling',
      serviceType: CapaQuotationServiceType.Cnc,
      detailedServiceType: CapaQuotationDetailedServiceType.Drilling,
      log: Enum_Action_Button_Enum.Landing_CNC_DRILLING
    }
  ],

  [CapaQuotationServiceType.Printing]: [
    {
      img: processmethodImages.PROCESSMETHOD_PRINTING_SLA,
      label: 'SLA',
      serviceType: CapaQuotationServiceType.Printing,
      detailedServiceType: CapaQuotationDetailedServiceType.Sla,
      log: Enum_Action_Button_Enum.Landing_3DP_SLA
    },
    {
      img: processmethodImages.PROCESSMETHOD_PRINTING_FDM,
      label: 'FDM',
      serviceType: CapaQuotationServiceType.Printing,
      detailedServiceType: CapaQuotationDetailedServiceType.Fdm,
      log: Enum_Action_Button_Enum.Landing_3DP_FDM
    },
    {
      img: processmethodImages.PROCESSMETHOD_PRINTING_POLYJET,
      label: 'Polyjet',
      serviceType: CapaQuotationServiceType.Printing,
      detailedServiceType: CapaQuotationDetailedServiceType.Polyjet,
      log: Enum_Action_Button_Enum.Landing_3DP_POLYJET
    },
    {
      img: processmethodImages.PROCESSMETHOD_PRINTING_MJF,
      label: 'MJF',
      serviceType: CapaQuotationServiceType.Printing,
      detailedServiceType: CapaQuotationDetailedServiceType.Mjf,
      log: Enum_Action_Button_Enum.Landing_3DP_MJF
    },
    {
      img: processmethodImages.PROCESSMETHOD_PRINTING_SLS,
      label: 'SLS',
      serviceType: CapaQuotationServiceType.Printing,
      detailedServiceType: CapaQuotationDetailedServiceType.Sls,
      log: Enum_Action_Button_Enum.Landing_3DP_SLS
    },
    {
      img: processmethodImages.PROCESSMETHOD_PRINTING_SLM_DMLS,
      label: 'SLM / DMLS',
      serviceType: CapaQuotationServiceType.Printing,
      detailedServiceType: CapaQuotationDetailedServiceType.SlmDmls,
      log: Enum_Action_Button_Enum.Landing_3DP_SLM
    }
  ],

  [CapaQuotationServiceType.Inj]: [
    {
      img: processmethodImages.PROCESSMETHOD_MOLDING_INJECTION,
      label: '일반 사출',
      english: 'Injection Molding',
      serviceType: CapaQuotationServiceType.Inj,
      detailedServiceType: CapaQuotationDetailedServiceType.InjectionMolding,
      log: Enum_Action_Button_Enum.Landing_INJ_IM
    },
    {
      img: processmethodImages.PROCESSMETHOD_MOLDING_DOUBLE_INJECTION,
      label: '이중 사출',
      english: 'Twin Shot',
      serviceType: CapaQuotationServiceType.Inj,
      detailedServiceType:
        CapaQuotationDetailedServiceType.DoubleShotInjectionMolding,
      log: Enum_Action_Button_Enum.Landing_INJ_TS
    },
    {
      img: processmethodImages.PROCESSMETHOD_MOLDING_BLOW_INJECTION,
      label: '블로우 사출',
      english: 'Blow Injection Molding',
      serviceType: CapaQuotationServiceType.Inj,
      detailedServiceType:
        CapaQuotationDetailedServiceType.BlowInjectionMolding,
      log: Enum_Action_Button_Enum.Landing_INJ_BIM
    },
    {
      img: processmethodImages.PROCESSMETHOD_MOLDING_INSERT_INJECTION,
      label: '인서트 사출',
      english: 'Insert Injection Molding',
      serviceType: CapaQuotationServiceType.Inj,
      detailedServiceType:
        CapaQuotationDetailedServiceType.InsertInjectionMolding,
      log: Enum_Action_Button_Enum.Landing_INJ_IIM
    }
  ],

  [CapaQuotationServiceType.SheetMetal]: [
    {
      img: processmethodImages.PROCESSMETHOD_SHEETMETAL_PRESS,
      label: '프레스',
      english: 'Press',
      serviceType: CapaQuotationServiceType.SheetMetal,
      detailedServiceType: CapaQuotationDetailedServiceType.Press,
      log: Enum_Action_Button_Enum.Landing_SM_PROCESS_METHOD_PRESS
    },
    {
      img: processmethodImages.PROCESSMETHOD_SHEETMETAL_LASER_CUTTING,
      label: '레이저 커팅',
      english: 'Laser Cutting',
      serviceType: CapaQuotationServiceType.SheetMetal,
      detailedServiceType: CapaQuotationDetailedServiceType.LaserCutting,
      log: Enum_Action_Button_Enum.Landing_SM_PROCESS_METHOD_LC
    },
    {
      img: processmethodImages.PROCESSMETHOD_SHEETMETAL_WATERJET_CUTTING,
      label: '워터젯 커팅',
      english: 'Waterjet Cutting',
      serviceType: CapaQuotationServiceType.SheetMetal,
      detailedServiceType: CapaQuotationDetailedServiceType.WaterjetCutting,
      log: Enum_Action_Button_Enum.Landing_SM_PROCESS_METHOD_WC
    }
  ],

  [CapaQuotationServiceType.Casting]: [
    {
      img: processmethodImages.PROCESSMETHOD_CASTING_SAND,
      label: '사형 주조',
      english: 'Sand Casting',
      serviceType: CapaQuotationServiceType.Casting,
      detailedServiceType: CapaQuotationDetailedServiceType.SandCasting,
      log: Enum_Action_Button_Enum.Landing_CASTING_PROCESS_METHOD_SAND
    },
    {
      img: processmethodImages.PROCESSMETHOD_CASTING_SHELL,
      label: '쉘 주조',
      english: 'Shell Casting',
      serviceType: CapaQuotationServiceType.Casting,
      detailedServiceType: CapaQuotationDetailedServiceType.ShellCasting,
      log: Enum_Action_Button_Enum.Landing_CASTING_PROCESS_METHOD_SHELL
    },
    {
      img: processmethodImages.PROCESSMETHOD_CASTING_INVESTMENT,
      label: '인베스트먼트 주조',
      english: 'Investment Casting',
      serviceType: CapaQuotationServiceType.Casting,
      detailedServiceType: CapaQuotationDetailedServiceType.InvestmentCasting,
      log: Enum_Action_Button_Enum.Landing_CASTING_PROCESS_METHOD_IC
    },
    {
      img: processmethodImages.PROCESSMETHOD_CASTING_DIE,
      label: '다이 캐스팅',
      english: 'Die Casting',
      serviceType: CapaQuotationServiceType.Casting,
      detailedServiceType: CapaQuotationDetailedServiceType.DieCasting,
      log: Enum_Action_Button_Enum.Landing_CASTING_PROCESS_METHOD_DC
    },
    {
      img: processmethodImages.PROCESSMETHOD_CASTING_CENTRIFUGAL,
      label: '원심 주조',
      english: 'Centrifugal Casting',
      serviceType: CapaQuotationServiceType.Casting,
      detailedServiceType: CapaQuotationDetailedServiceType.CentrifugalCasting,
      log: Enum_Action_Button_Enum.Landing_CASTING_PROCESS_METHOD_CC
    }
  ],
  [CapaQuotationServiceType.Bending]: [],
  [CapaQuotationServiceType.Weilding]: [],
  [CapaQuotationServiceType.Prototyping]: [],
  [CapaQuotationServiceType.MechanicalDesign]: [],
  [CapaQuotationServiceType.ProductDesign]: [],
  [CapaQuotationServiceType.Pcb]: [
    {
      img: assets.PCBProcessDesign,
      label: '설계',
      serviceType: CapaQuotationServiceType.Pcb,
      detailedServiceType: CapaPcbServiceType.CircuitDesign,
      log: Enum_Action_Button_Enum.Landing_PCB_SERVICE_DESIGN
    },
    {
      img: assets.ServiceType_Pcb_192,
      label: '제작',
      serviceType: CapaQuotationServiceType.Pcb,
      detailedServiceType: CapaPcbServiceType.CircuitFabrication,
      log: Enum_Action_Button_Enum.Landing_PCB_SERVICE_FABRICATION
    },
    {
      img: assets.PCBProcessAssemble,
      label: '조립',
      serviceType: CapaQuotationServiceType.Pcb,
      detailedServiceType: CapaPcbServiceType.CircuitAssembly,
      log: Enum_Action_Button_Enum.Landing_PCB_SERVICE_ASSEMBLY
    }
  ],
  [CapaQuotationServiceType.Design]: []
};
